import { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { SplitButtonOption } from '../../../components/general/SplitButton/SplitButton.tsx';
import { getStoredOption } from '../../../components/general/SplitButton/util/localStorageHelpers.ts';

export type SaveMode = 'save' | 'save_and_next';

export const SAVE_MODE_OPTIONS: SplitButtonOption[] = [
    { id: 'save', label: 'Opslaan' },
    { id: 'save_and_next', label: 'Opslaan en volgende' },
];

const SAVE_MODE_IDS = SAVE_MODE_OPTIONS.map((option) => option.id);

export type SaveModePreference = {
    storageKey: string;
    showChoice: boolean;
    mode: SaveMode;
    label: string;
    select: (mode: string) => void;
};

function storageKeyForPath(pathname: string): string {
    const page = pathname.split('/').filter(Boolean)[0] ?? 'root';
    return `document_editor_save_mode:${page}`;
}

export function useSaveModePreference(canSaveAndNext: boolean, saveAndNextOnly: boolean): SaveModePreference {
    const { pathname } = useLocation();
    const storageKey = storageKeyForPath(pathname);
    const [stored, setStored] = useState(() => ({ key: storageKey, mode: getStoredOption(storageKey, SAVE_MODE_IDS, 'save') }));
    const remembered = stored.key === storageKey ? stored.mode : getStoredOption(storageKey, SAVE_MODE_IDS, 'save');
    const select = useCallback((mode: string) => setStored({ key: storageKey, mode }), [storageKey]);
    const wantsNext = saveAndNextOnly || remembered === 'save_and_next';
    const mode: SaveMode = canSaveAndNext && wantsNext ? 'save_and_next' : 'save';
    return {
        storageKey,
        showChoice: canSaveAndNext && !saveAndNextOnly,
        mode,
        label: SAVE_MODE_OPTIONS.find((option) => option.id === mode)?.label ?? 'Opslaan',
        select,
    };
}
